import type { SetStateAction } from 'react';
import { useState } from 'react';
import { Link } from 'react-router';
import type { LoginModalType, PostAuthOptions } from 'types';
import type { Image } from 'types/capi/shared';

import ChevronIcon from '~/assets/icons/Chevron';
import VectorIcon from '~/assets/icons/Vector';
import WarningIcon from '~/assets/icons/Warning';
import useUser from '~/hooks/useUser';
import { isAppleMobileDevice, isInAppBrowser } from '~/utils/browser';

import { Dialog, DialogDismiss } from '../../../Dialog';
import DownloadAppFooter from '../DownloadAppFooter';
import ForgotPasswordScreen from './ForgotPasswordScreen';
import OrSeparator from './OrSeperator';
import OtherLoginOptionsMessage from './OtherLoginOptionsMessage';
import SignInScreen from './SignInScreen';

export enum Stage {
  Error = -1,
  Initial = 0,
  ForgotPass = 1
}

type Props = {
  hideAppDownloadPrompt?: boolean;
  image?: Image;
  modalType: LoginModalType;
  onBackNavigation?: () => void;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  postAuthOptions?: PostAuthOptions;
};

export default function EmailLogin({
  onDismiss,
  postAuthOptions,
  modalType,
  onBackNavigation,
  hideAppDownloadPrompt = false
}: Props) {
  const [stage, setStage] = useState(Stage.Initial);
  const [error, setError] = useState<string | null>(null);
  const user = useUser();

  if (user.isLoggedIn) return null;

  const showAppDownload =
    isAppleMobileDevice(navigator.userAgent) &&
    !hideAppDownloadPrompt &&
    stage === Stage.Initial &&
    !['purchase', 'profile'].includes(modalType);

  const isInApp = isInAppBrowser(navigator.userAgent);

  function onBackNavigationOverride() {
    if (stage === Stage.Initial && onBackNavigation) return onBackNavigation();

    setError(null);
    setStage(stage - 1);
  }

  return (
    <Dialog
      isOpen={true}
      onDismiss={onDismiss}
      className="z-50 rounded-lg border border-white bg-black p-4"
    >
      <div className="my-4 flex w-auto max-w-[500px] flex-col">
        <nav className="flex flex-row items-center justify-between">
          {onBackNavigation && (
            <button className="rotate-90" onClick={onBackNavigationOverride}>
              <ChevronIcon stroke="white" width="14" height="14" />
            </button>
          )}
          {modalType === 'profile' ? (
            <Link to="/">
              <VectorIcon fill="white" width="10" height="10" />
            </Link>
          ) : (
            <DialogDismiss className="text-black">
              <VectorIcon fill="white" width="10" height="10" />
            </DialogDismiss>
          )}
        </nav>

        <Screen
          onDismiss={onDismiss}
          stage={stage}
          postAuthOptions={postAuthOptions}
          onError={setError}
          setStage={setStage}
        />

        {error && <ErrorMessage error={error} isWhiteBackground={false} />}
        {showAppDownload && <DownloadAppFooter modalType={modalType} />}
        {isInApp && (
          <>
            <OrSeparator />
            <OtherLoginOptionsMessage isOnBlack={stage === Stage.Initial} />
          </>
        )}
      </div>
    </Dialog>
  );
}

export type ScreenProps = {
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  onError: (err: string | null) => void;
  postAuthOptions?: PostAuthOptions;
  setStage: React.Dispatch<SetStateAction<Stage>>;
  stage: Stage;
};

function Screen(props: ScreenProps) {
  switch (props.stage) {
    case Stage.Initial:
      return <SignInScreen {...props} />;
    case Stage.ForgotPass:
      return <ForgotPasswordScreen />;
  }
}

function ErrorMessage({
  error,
  isWhiteBackground
}: {
  error: string;
  isWhiteBackground: boolean;
}) {
  return (
    <p
      className={`mt-4 flex items-center gap-2 rounded p-2 text-sm font-normal ${
        isWhiteBackground
          ? 'bg-red-alert/10 text-red-alert'
          : 'bg-red-alert/75 text-white'
      }`}
    >
      <span className="w-6">
        <WarningIcon fill={isWhiteBackground ? '#D71225' : 'white'} />
      </span>
      {error}
    </p>
  );
}
